import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import * as XLSX from 'xlsx';
import Pagination from '../common/Pagination/Pagination';
import { usePackingLists } from '../../queries/packingListQueries';
import { useDeletePackingList } from '../../mutations/packingListMutations';
import { extractItems, extractPagination } from '../../lib/apiHelpers';
import DownloadReportModal from '../popupmodal/DownloadReportModal';
import { isAdmin, isEditableToday } from '../../lib/roleUtils';

// Removed static SAMPLE_PACKING_LISTS mock data for clean server-side integration

const AgentManagement = () => {
    // ─── User Role Check ───────────────────────────────────────────────────────
    const userRole = sessionStorage.getItem('userRole') ?? '';
    const isUserAdmin = isAdmin(userRole);

    // ─── Local State ──────────────────────────────────────────────────────────
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedAgent, setSelectedAgent] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRowIds, setSelectedRowIds] = useState(new Set());
    const [showReportModal, setShowReportModal] = useState(false);
    const [activeReportRow, setActiveReportRow] = useState(null);

    const handleOpenReportModal = (row) => {
        setActiveReportRow(row);
        setShowReportModal(true);
    };
    const handleCloseReportModal = () => {
        setShowReportModal(false);
        setActiveReportRow(null);
    };

    // Debounce search term changes to prevent spamming the API
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // ─── React Query: Fetch packing lists from API (server-side pagination & filter) 
    const { data: apiData, isLoading, isError } = usePackingLists(
        currentPage,
        debouncedSearch,
        selectedAgent,
        selectedStatus
    );
    const deleteMutation = useDeletePackingList();

    // Unwrap the paginated envelope: { data:[], totalPages, ... }
    const packingLists = useMemo(() => extractItems(apiData), [apiData]);
    const pagination = useMemo(() => extractPagination(apiData), [apiData]);
    const totalPages = pagination.totalPages;

    const navigate = useNavigate();

    // ─── Extract Filter Options Dynamically ────────────────────────────────────
    const agentOptions = useMemo(() => {
        const agents = new Set();
        packingLists.forEach((row) => {
            const agent = row.agentName || row.agent;
            if (agent) agents.add(agent);
        });
        return ['', ...Array.from(agents)];
    }, [packingLists]);

    const statusOptions = useMemo(() => {
        const statuses = new Set();
        packingLists.forEach((row) => {
            const status = row.status;
            if (status) statuses.add(status.toLowerCase());
        });
        return ['', ...Array.from(statuses)];
    }, [packingLists]);

    const paginatedPackingLists = packingLists; // server already paged

    // ─── Row Selection Logic ──────────────────────────────────────────────────
    const getRowId = (row) => row.id;

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIdsOnPage = paginatedPackingLists.map((row) => getRowId(row));
            setSelectedRowIds(new Set([...selectedRowIds, ...allIdsOnPage]));
        } else {
            const newSelected = new Set(selectedRowIds);
            paginatedPackingLists.forEach((row) => newSelected.delete(getRowId(row)));
            setSelectedRowIds(newSelected);
        }
    };

    const handleSelectRow = (id) => {
        const newSelected = new Set(selectedRowIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedRowIds(newSelected);
    };

    const isAllSelectedOnPage = useMemo(() => {
        if (paginatedPackingLists.length === 0) return false;
        return paginatedPackingLists.every((row) => selectedRowIds.has(getRowId(row)));
    }, [paginatedPackingLists, selectedRowIds]);

    // ─── Delete Selected Rows ──────────────────────────────────────────────────
    const handleDeleteSelected = () => {
        const idsToDelete = Array.from(selectedRowIds);
        if (idsToDelete.length === 0) return;

        Promise.all(idsToDelete.map((id) => deleteMutation.mutateAsync(id)))
            .then(() => {
                setSelectedRowIds(new Set()); // clear selection after delete
            })
            .catch(() => {
                // errors are handled by mutation onError
            });
    };

    // ─── Totals Calculation ──────────────────────────────────────────────────
    const totals = useMemo(() => {
        let totalCbm = 0;
        let totalComm = 0;
        let totalLoad = 0;
        let totalReceived = 0;
        let totalPaid = 0;
        let totalBalance = 0;

        packingLists.forEach((row) => {
            totalCbm += Number(row.cbm) || 0;
            totalComm += Number(row.comm) || 0;
            totalLoad += Number(row.loadvalue ?? row.load) || 0;
            totalReceived += Number(row.received) || 0;
            totalPaid += Number(row.paidAmount ?? row.paid) || 0;
            totalBalance += Number(row.balanceAmount ?? row.balance) || 0;
        });

        return {
            cbm: totalCbm.toFixed(3),
            comm: totalComm.toFixed(2),
            load: totalLoad.toFixed(2),
            received: totalReceived.toFixed(2),
            paid: totalPaid.toFixed(2),
            balance: totalBalance.toFixed(2),
        };
    }, [packingLists]);

    // ─── Formatting Helpers ──────────────────────────────────────────────────
    const formatMoney = (val) => {
        if (val === undefined || val === null || val === "" || val === "-") return "-";
        const num = Number(val);
        if (isNaN(num)) return val;
        return num.toFixed(2);
    };

    const formatCbm = (val) => {
        if (val === undefined || val === null || val === "") return "-";
        const num = Number(val);
        if (isNaN(num)) return val;
        return num.toFixed(3);
    };

    const formatDate = (val) => {
        if (!val || val === '-') return '-';
        const m = moment(val);
        return m.isValid() ? m.format('DD-MM-YYYY') : '-';
    };

    const downloadExcel = () => {
        const headers = [
            "S.no",
            "FILE ID",
            "DATE",
            "AGENT",
            "PARTY NAME",
            "IMPORTER",
            "PKL",
            "CBM",
            "COMM",
            "LOAD",
            "RECEIVED",
            "PAID",
            "BALANCE",
            "STATUS",
        ];

        const selectedRows = packingLists.filter(row => selectedRowIds.has(row.id || row._id));
        const dataToExport = selectedRows.length !== 0 ? selectedRows : packingLists;

        const formattedData = dataToExport.map((item, index) => ({
            "S.no": index + 1,
            "FILE ID": item.fileId || "",
            DATE: formatDate(item.plDate ?? item.date),
            AGENT: item.agentName || item.agent || "",
            "PARTY NAME": item.partyName || "",
            IMPORTER: item.importer || "",
            PKL: item.pkl || "",
            CBM: item.cbm || "",
            COMM: item.comm || "",
            LOAD: (item.loadvalue ?? item.load) || "",
            RECEIVED: item.received || "",
            PAID: (item.paidAmount ?? item.paid) || "",
            BALANCE: (item.balanceAmount ?? item.balance) || "",
            STATUS: item.status || "",
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(formattedData, { header: headers });

        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
        XLSX.writeFile(wb, "Data.xlsx");
    };

    return (
        <div className="d-flex flex-column w-100 h-100">
            {/* ══ Enterprise Toolbar ══ */}
            <div className="gtss-toolbar-strip">

                {/* ── Left: Module Title ── */}
                <div className="d-flex flex-column me-1" style={{ flexShrink: 0 }}>
                    <span className="gtss-page-title">
                        {isUserAdmin ? 'PKL Management' : 'Agent Management'}
                    </span>
                    <span className="gtss-page-subtitle">Packing List Records</span>
                </div>

                {/* ── Divider ── */}
                <div className="gtss-divider"></div>

                {/* ── Search ── */}
                <div className="input-group" style={{ width: '220px' }}>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search File ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <span className="input-group-text">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
                        </svg>
                    </span>
                </div>

                {/* ── Divider ── */}
                <div className="gtss-divider"></div>

                {/* ── Filters ── */}
                <div className="d-flex gap-2 align-items-center">
                    <select
                        className="form-select form-select-dark"
                        value={selectedAgent}
                        onChange={(e) => {
                            setSelectedAgent(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="">Agent</option>
                        {agentOptions.filter(Boolean).map((agent) => (
                            <option key={agent} value={agent}>{agent}</option>
                        ))}
                    </select>

                    {!isUserAdmin && (
                        <select
                            className="form-select form-select-dark"
                            value={selectedStatus}
                            onChange={(e) => {
                                setSelectedStatus(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">Status</option>
                            {statusOptions.filter(Boolean).map((status) => (
                                <option key={status} value={status}>{status.toUpperCase()}</option>
                            ))}
                        </select>
                    )}
                </div>

                {/* ── Spacer ── */}
                <div className="flex-grow-1"></div>

                {/* ── Actions (Super Admin / Full view only) ── */}
                {!isUserAdmin && (
                    <div className="d-flex align-items-center gap-2 ms-auto">
                        {selectedRowIds.size > 0 && (
                            <button
                                type="button"
                                className="btn btn-danger btn-sm fw-semibold d-flex align-items-center gap-1"
                                onClick={handleDeleteSelected}
                                disabled={deleteMutation.isPending}
                                aria-label={`Delete ${selectedRowIds.size} selected packing list(s)`}
                            >
                                {deleteMutation.isPending ? (
                                    <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Deleting...</>
                                ) : (
                                    `Delete (${selectedRowIds.size})`
                                )}
                            </button>
                        )}

                        <button
                            type="button"
                            className="btn btn-orange"
                            onClick={() => navigate('/business/packing-list')}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
                            </svg>
                            New PKL
                        </button>
                        <button type="button" className="btn btn-white-outline" onClick={downloadExcel}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                            </svg>
                            Export
                        </button>
                    </div>
                )}

            </div>

            {/* ── Table Container — fills remaining height ── */}
            <div className="flex-grow-1 overflow-hidden d-flex flex-column gtss-view-container">
                <div className="border rounded-3 overflow-hidden bg-white d-flex flex-column flex-grow-1">
                    {isError && (
                        <div className="alert alert-danger mb-0 rounded-0 py-2 text-center" role="alert" style={{ fontSize: '14px' }}>
                            <strong className="d-block d-sm-inline">Failed to load packing lists.</strong> Showing sample data instead.
                        </div>
                    )}
                    <div className="table-responsive flex-grow-1">
                        <table className="table table-hover align-middle mb-0 gtss-table text-nowrap agent-management-table">
                            <thead className="gtss-bg-primary text-white text-uppercase">
                                {isUserAdmin ? (
                                    /* Admin Role 6-Column Header */
                                    <tr>
                                        <th className="py-2 text-center" style={{ textAlign: 'center' }}>EDIT</th>
                                        <th className="py-2 text-center" style={{ textAlign: 'center' }}>CONTAINER NO</th>
                                        <th className="py-2 text-center" style={{ textAlign: 'center' }}>PKL NO</th>
                                        <th className="py-2 text-center" style={{ textAlign: 'center' }}>LORRY NO</th>
                                        <th className="py-2 text-center" style={{ textAlign: 'center' }}>AGENT</th>
                                        <th className="py-2 text-center" style={{ textAlign: 'center' }}>DATE</th>
                                    </tr>
                                ) : (
                                    /* Super Admin Full 15-Column Header */
                                    <tr>
                                        <th className="py-2 px-1 text-center sticky-col-header" style={{ width: '36px', minWidth: '36px', left: 0 }}>
                                            <div className="d-flex align-items-center justify-content-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input mt-0"
                                                    aria-label="Select all rows"
                                                    checked={isAllSelectedOnPage}
                                                    onChange={handleSelectAll}
                                                />
                                            </div>
                                        </th>
                                        <th className="py-2 text-center sticky-col-header" style={{ width: '40px', minWidth: '40px', left: '36px' }}>S.NO</th>
                                        <th className="py-2 text-center sticky-col-header" style={{ width: '100px', minWidth: '100px', left: '76px' }}>FILE ID</th>
                                        <th className="py-2 text-center">DATE</th>
                                        <th className="py-2 text-center" style={{ width: '120px', minWidth: '120px', whiteSpace: 'normal' }}>AGENT</th>
                                        <th className="py-2 text-start" style={{ width: '120px', minWidth: '120px', whiteSpace: 'normal' }}>PARTY NAME</th>
                                        <th className="py-2 text-start" style={{ width: '120px', minWidth: '120px', whiteSpace: 'normal' }}>IMPORTER</th>
                                        <th className="py-2 text-end">PKL</th>
                                        <th className="py-2 text-end">CBM</th>
                                        <th className="py-2 text-end">COMM</th>
                                        <th className="py-2 text-end">LOAD</th>
                                        <th className="py-2 text-end">RECEIVED</th>
                                        <th className="py-2 text-end">PAID</th>
                                        <th className="py-2 text-end">BALANCE</th>
                                        <th className="py-2 text-center">STATUS</th>
                                        <th className="py-2 text-center">REPORT</th>
                                    </tr>
                                )}
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={isUserAdmin ? 6 : 16} className="py-5 text-center">
                                            <div className="d-flex justify-content-center align-items-center gap-2">
                                                <div className="spinner-border spinner-border-sm" role="status" style={{ color: '#1B4F8A' }}>
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span className="text-muted">Loading packing lists...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : paginatedPackingLists.length > 0 ? (
                                    paginatedPackingLists.map((row, idx) => {
                                        const serialNo = (currentPage - 1) * pagination.pageSize + idx + 1;
                                        const rowId = getRowId(row);
                                        const isSelected = selectedRowIds.has(rowId);
                                        const isEditable = isEditableToday(row.plDate ?? row.date);

                                        if (isUserAdmin) {
                                            /* Admin Role 6-Column Row */
                                            return (
                                                <tr key={rowId}>
                                                    {/* EDIT Icon (matching Data Management style) */}
                                                    <td className="text-center" style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            type="button"
                                                            className="btn p-0 border-0 bg-transparent dm-icon-btn"
                                                            onClick={() => {
                                                                if (isEditable) {
                                                                    navigate('/business/packing-list', { state: row });
                                                                }
                                                            }}
                                                            disabled={!isEditable}
                                                            style={{
                                                                cursor: isEditable ? 'pointer' : 'not-allowed',
                                                                opacity: isEditable ? 1 : 0.5,
                                                            }}
                                                            title={isEditable ? 'Click to edit' : 'Editing disabled for this date'}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                                <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.5.5 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11z"/>
                                                            </svg>
                                                        </button>
                                                    </td>
                                                    <td className="text-center fw-semibold" style={{ textAlign: 'center' }}>{row.container || row.containerNo || row.containersNumber || '-'}</td>
                                                    <td className="text-center" style={{ textAlign: 'center' }}>{row.pkl || '-'}</td>
                                                    <td className="text-center" style={{ textAlign: 'center' }}>{row.lorry || '-'}</td>
                                                    <td className="text-center fw-semibold" style={{ textAlign: 'center' }}>{row.agentName || row.agent || '-'}</td>
                                                    <td className="text-center" style={{ textAlign: 'center' }}>{formatDate(row.plDate ?? row.date)}</td>
                                                </tr>
                                            );
                                        }

                                        /* Super Admin Full Row */
                                        return (
                                            <tr
                                                key={rowId}
                                                className={isSelected ? 'table-primary-subtle' : ''}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => navigate('/business/packing-list', { state: row })}
                                            >
                                                <td className="px-1 text-center sticky-col-body" style={{ width: '36px', minWidth: '36px', left: 0 }} onClick={(e) => e.stopPropagation()}>
                                                    <div className="d-flex align-items-center justify-content-center">
                                                        <input
                                                            className="form-check-input mt-0"
                                                            type="checkbox"
                                                            aria-label="Select row"
                                                            checked={isSelected}
                                                            onChange={() => handleSelectRow(rowId)}
                                                        />
                                                    </div>
                                                </td>
                                                <td className="text-muted fw-semibold text-center sticky-col-body" style={{ width: '40px', minWidth: '40px', left: '36px' }}>{serialNo}</td>
                                                <td className="fw-semibold text-center sticky-col-body" style={{ width: '100px', minWidth: '100px', left: '76px' }}>{row.fileId || '-'}</td>
                                                <td className="text-center">{formatDate(row.plDate ?? row.date)}</td>
                                                <td className="fw-semibold text-center" style={{ width: '120px', minWidth: '120px', whiteSpace: 'normal' }}>{row.agentName || row.agent || '-'}</td>
                                                <td className="text-start" style={{ width: '120px', minWidth: '120px', whiteSpace: 'normal' }}>
                                                    <span className="fw-semibold">{row.partyName || '-'}</span>
                                                </td>
                                                <td className="text-start" style={{ width: '120px', minWidth: '120px', whiteSpace: 'normal' }}>
                                                    <span className="fw-semibold">{row.importer || '-'}</span>
                                                </td>
                                                <td className="text-end">{row.pkl || '-'}</td>
                                                <td className="text-end">{formatCbm(row.cbm)}</td>
                                                <td className="text-end">{formatMoney(row.comm)}</td>
                                                <td className="text-end">{formatMoney(row.loadvalue ?? row.load)}</td>
                                                <td className="text-end">{formatMoney(row.received)}</td>
                                                <td className="text-end">{formatMoney(row.paidAmount ?? row.paid)}</td>
                                                <td className="text-end">{formatMoney(row.balanceAmount ?? row.balance)}</td>
                                                <td className="text-center">
                                                    <span className="fw-semibold text-uppercase">{row.status || 'active'}</span>
                                                </td>
                                                <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        className="btn btn-sm text-white gtss-bg-primary fw-semibold px-3 py-1 border-0 rounded-2"
                                                        onClick={() => handleOpenReportModal(row)}
                                                    >
                                                        DOWNLOAD REPORT
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={isUserAdmin ? 6 : 16} className="py-4 text-center text-muted">
                                            No records found matching the search criteria.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {!isUserAdmin && (
                                <tfoot className="gtss-bg-primary text-white fw-bold">
                                    <tr>
                                        <td className="sticky-col-header" style={{ left: 0, width: '36px', minWidth: '36px' }}></td>
                                        <td className="sticky-col-header" style={{ left: '36px', width: '40px', minWidth: '40px' }}></td>
                                        <td className="sticky-col-header text-center" style={{ left: '76px', width: '100px', minWidth: '100px' }}>Total</td>
                                        <td></td>
                                        <td style={{ width: '120px', minWidth: '120px' }}></td>
                                        <td style={{ width: '120px', minWidth: '120px' }}></td>
                                        <td style={{ width: '120px', minWidth: '120px' }}></td>
                                        <td className="text-end">-</td>
                                        <td className="text-end">{totals.cbm !== "0.000" ? totals.cbm : "0"}</td>
                                        <td className="text-end">{totals.comm !== "0.00" ? totals.comm : "-"}</td>
                                        <td className="text-end">{totals.load !== "0.00" ? totals.load : "-"}</td>
                                        <td className="text-end">{totals.received !== "0.00" ? totals.received : "-"}</td>
                                        <td className="text-end">{totals.paid !== "0.00" ? totals.paid : "-"}</td>
                                        <td className="text-end">{totals.balance !== "0.00" ? totals.balance : "-"}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>

                    {/* ── Pagination Footer ── */}
                    {totalPages >= 1 && (
                        <div className="border-top bg-white py-3">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={(page) => setCurrentPage(page)}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* ── Download Report Modal ── */}
            <DownloadReportModal
                isOpen={showReportModal}
                onClose={handleCloseReportModal}
                row={activeReportRow}
                onDownload={(key, row) => {
                    if (key === 'pkl') {
                        handleCloseReportModal();
                        navigate(`/business/pkl-download/${row._id || row.id}`);
                    } else if (key === 'mgirth') {
                        handleCloseReportModal();
                        navigate(`/business/mgirth-pkl-download/${row._id || row.id}`);
                    } else if (key === 'ac') {
                        handleCloseReportModal();
                        navigate(`/business/ac-download/${row._id || row.id}`);
                    } else {
                        // Placeholder for other report downloads
                        console.log('Downloading:', key, row);
                    }
                }}
            />
        </div>
    );
};

export default AgentManagement;